import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { breadcrumbs, definedTermSet, aeoGeo } from "@/lib/seo/schema";
import { DEFINITIONS } from "@/content/definitions";

const TERMS: { term: string; description: string }[] = [
  {
    term: "Brand film",
    description:
      "Längre film som bygger varumärke och känsla snarare än säljer en enskild produkt. Fokus på story, ton och regi.",
  },
  {
    term: "Reklamfilm",
    description:
      "Kort, säljdriven film med tydlig avsändare och call-to-action. Körs ofta i betalda kanaler (YouTube, Meta, TV, biograf).",
  },
  {
    term: "Dokumentär stil",
    description:
      "Filmgrepp där riktiga människor och miljöer bär berättelsen, utan manus eller skådespelare. Ger trovärdighet och djup.",
  },
  {
    term: "Sizzle reel",
    description:
      "Snabbklippt högenergikollage av tidigare arbeten, ofta 30–60 sekunder, använt som säljmaterial eller event-opener.",
  },
  {
    term: "EPK (Electronic Press Kit)",
    description:
      "Paket med film, bild och text som distribueras till press inför en lansering eller release.",
  },
  {
    term: "Förklarvideo (explainer)",
    description:
      "Kort animerad eller filmad video som förklarar en produkt, tjänst eller idé på 60–120 sekunder.",
  },
  {
    term: "Employer branding-film",
    description:
      "Film som bygger arbetsgivarvarumärke. Vanligast: medarbetar-porträtt, kontorsturer, kultur-berättelser för rekrytering.",
  },
  {
    term: "Versionering",
    description:
      "Att skapa flera klipp och format (16:9, 9:16, 1:1, kort/lång) från samma huvudfilm för olika kanaler.",
  },
  {
    term: "Color grade",
    description:
      "Färgsättning av filmen i post-produktion. Bestämmer ton, tid på dygnet och kvalitetskänsla.",
  },
  {
    term: "Pulse (filmabonnemang)",
    description:
      "Fennys löpande leveransmodell — en förutsägbar månadsvolym brand- och social-film utan projektfriktion.",
  },
  {
    term: "Prime (produktion)",
    description:
      "Fennys skräddarsydda produktionsmodell för enskilda större filmer: reklam, dokumentär, employer branding.",
  },
  {
    term: "Retainer",
    description:
      "Återkommande abonnemangsavtal med fast månadsavgift och definierad leverans, istället för enskilda offerter.",
  },
  {
    term: "Hook",
    description:
      "De första 1–3 sekunderna i en social film. Avgör om tittaren stannar eller scrollar vidare.",
  },
  {
    term: "Aspect ratio",
    description:
      "Bildformat. 16:9 (liggande/YouTube), 9:16 (stående/Reels & TikTok), 1:1 (kvadrat/feed).",
  },
];

export const Route = createFileRoute("/ordlista")({
  head: () => ({
    meta: [
      { title: "Ordlista — filmproduktion & video marketing | Fenny" },
      {
        name: "description",
        content:
          "Filmbranschens vanligaste begrepp på svenska — från brand film, sizzle reel och retainer till hook, B-roll och versionering. Praktisk ordlista för marknad och sälj.",
      },
      {
        property: "og:title",
        content: "Ordlista — filmproduktion & video marketing | Fenny",
      },
      {
        property: "og:description",
        content:
          "Filmbranschens vanligaste begrepp förklarade på svenska. För marknadsteam, grundare och förbund.",
      },
      { property: "og:url", content: "https://fenny.se/ordlista" },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/ordlista" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/ordlista", name: "Ordlista — filmproduktion & video marketing | Fenny", description: "Filmbranschens vanligaste begrepp på svenska — från brand film, sizzle reel och retainer till hook, B-roll och versionering. Praktisk ordlista för marknad och sälj."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          definedTermSet("Fenny — filmordlista", "/ordlista", TERMS),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Ordlista", path: "/ordlista" },
          ]),
        ),
      },
    ],
  }),
  component: GlossaryPage,
});

function GlossaryPage() {
  return (
    <Section className="pt-32 pb-20">
      <div className="container-wide max-w-3xl">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Ordlista
        </p>
        <h1 className="mt-3 text-4xl md:text-5xl font-semibold tracking-tight">
          Filmbegrepp på svenska
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Kort referens till de begrepp som dyker upp när vi pratar produktion,
          format och leverans. Tänkt som uppslag — inte lärobok.
        </p>

        <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Fördjupade definitioner
          </h2>
          <ul className="mt-3 space-y-2">
            {DEFINITIONS.map((d) => (
              <li key={d.slug}>
                <Link
                  to={`/ordlista/${d.slug}` as "/ordlista/filmabonnemang"}
                  className="font-medium underline underline-offset-4"
                >
                  {d.term}
                </Link>
                <span className="text-muted-foreground"> — {d.definition}</span>
              </li>
            ))}
          </ul>
        </div>

        <dl className="mt-12 space-y-8">
          {TERMS.map((t) => (
            <div key={t.term} id={slugify(t.term)} className="scroll-mt-28">
              <dt className="text-lg font-semibold text-foreground">{t.term}</dt>
              <dd className="mt-1 text-muted-foreground">{t.description}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-16 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <p className="text-sm text-muted-foreground">
            Saknar du ett ord?{" "}
            <Link to="/kontakt" className="underline underline-offset-4">
              Skicka oss en rad
            </Link>{" "}
            så lägger vi till det.
          </p>
        </div>
      </div>
    </Section>
  );
}

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
